import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Container, Row, Column, Button, TextInput, Fieldset, Checkbox, Select, Alert, Icon } from '@pella/sash-framework';
import { LoadingOverlay, Image } from '../..';
import { getDirectory } from '../../../actions/location-actions';
import { lowesLocations, pellaLatitude, pellaLongitude } from '../../utilities/constants';
import styles from './index.module.scss';
import { isValidArray, isValidPostal } from '../../utilities/validations';
import { useLocation } from '../../../context/LocationContext';

const LocationsMap = ({ data, radius, include, className, containerWidth }) => {
	const showroomUrls = useStaticQuery(query).allContentstackBranchShowroom.nodes;
	const markerRef = useRef([]);
	const { setLocation, userZip, updateState } = useLocation();
	const [postalCode, setPostalCode] = useState(userZip || '');
	const [status, setStatus] = useState('');
	const [center, setCenter] = useState({ lat: pellaLatitude, lng: pellaLongitude });
	const [selectedPlace, setSelectedPlace] = useState(null);
	const [mapSettings, setMapSettings] = useState({ radius: radius, include: include });
	const [error, setError] = useState(null);
	const [directory, setDirectory] = useState({});
	const [loadMore, setLoadMore] = useState(true);

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
	});

	useEffect(() => {
		if (isValidPostal(userZip)) {
			setPostalCode(userZip);
			fetchDirectory(userZip, mapSettings.include, mapSettings.radius);
		}
	}, [userZip, mapSettings.include, mapSettings.radius]);

	const fetchDirectory = async (postal_code, include, radius) => {
		setStatus('loading');
		try {
			const directory = await getDirectory(postal_code, include, radius);

			if (!directory?.error) {
				const directoryData = directory?.data;

				if (directoryData.length < 8) setLoadMore(false);

				setDirectory(directoryData);
				setCenter({
					lat: directoryData[0]?.location?.coordinates?.[1],
					lng: directoryData[0]?.location?.coordinates?.[0],
				});
				setStatus('');
			} else {
				setStatus('error');
				setError([directory?.error?.message]);
			}
		} catch (error) {
			setStatus('error');
		}
	};

	const onPostalCodeChange = (event) => setPostalCode(event.target.value);

	const resetSelectedPlace = () => setSelectedPlace(null);

	const onZipFormSubmit = async (event) => {
		event.preventDefault();
		setError(null);
		updateState({ error: null });

		if (isValidPostal(postalCode)) {
			setStatus('loading');
			await setLocation(postalCode);
			fetchDirectory(postalCode, mapSettings.include, mapSettings.radius);
		} else {
			setError(['Please enter a valid postal code.']);
			updateState({ error: ['Please enter a valid postal code.'] });
		}
	};

	const onRadiusChange = (event) => {
		setError(null);
		resetSelectedPlace();
		fetchDirectory(postalCode, mapSettings.include, event.target.value);
		setMapSettings({ ...mapSettings, radius: event.target.value });
	};

	const onCheckboxChange = (event) => {
		setError(null);
		resetSelectedPlace();
		let filters = mapSettings.include;

		if (filters.includes(event.target.id)) {
			filters = filters.filter((store) => store !== event.target.id);
		} else {
			filters.push(event.target.id);
		}

		fetchDirectory(postalCode, filters, mapSettings.radius);
		setMapSettings({ ...mapSettings, include: filters });
	};

	const onMarkerClick = (id, marker) => {
		if (id >= 0) setSelectedPlace(id);

		if (marker && marker != null) {
			setCenter({
				lat: marker.props.position.lat,
				lng: marker.props.position.lng,
			});
		}
	};

	const onLoadMoreClick = () => setLoadMore(false);

	const onCorporateViewClick = () => {
		setPostalCode('50219');
		fetchDirectory('50219', mapSettings.include, mapSettings.radius);
		setMapSettings({ ...mapSettings, postal_code: '50219' });
	};

	return (
		<div className={`${className || ''} overflow-hidden`}>
			<Container width={containerWidth}>
				<div className='bg-white py-4 md:px-4 xl:p-8'>
					{data?.heading && <h2 className='mb-2 px-4 font-semibold uppercase md:px-0'>{data.heading}</h2>}
					{data?.description && <p className='mb-6 px-4 md:px-0'>{data.description}</p>}

					{error?.[0] && (
						<Alert
							className='mb-4'
							type='error'
							hideClose
						>
							{error.map((err, index) => (
								<p
									key={index}
									className='m-0'
								>
									{err}
								</p>
							))}
						</Alert>
					)}

					<div className={`${styles.googleMap_Grid} grid`}>
						<div className={`${styles.googleMap} relative mb-6 md:mb-0`}>
							{(!isLoaded || status === 'loading') && <LoadingOverlay fixed={false} />}

							{isLoaded && (
								<GoogleMap
									center={center}
									zoom={12}
									mapContainerClassName='h-full w-full'
									options={{
										mapTypeControl: false,
										streetViewControl: false,
										fullscreenControl: false,
									}}
								>
									{isValidArray(directory) &&
										directory.map((location, index) => {
											let icon;
											if (location.type === 'Showroom') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'Showrooms')?.file?.url + '?width=50';
											} else if (location.type === 'Lowes') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'Lowes')?.file?.url + '?width=40';
											} else if (location.type === 'PellaLum') {
												icon = data?.map_icons?.find((icon) => icon.location_type.toLowerCase() === location.name.toLowerCase())?.file?.url + '?width=40';
											} else if (location.level?.toLowerCase() === 'standard') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'PCC Certified')?.file?.url + '?width=40';
											} else if (location.level?.toLowerCase() === 'platinum') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'PCC Platinum')?.file?.url + '?width=40';
											} else if (location.level?.toLowerCase() === 'platinum elite') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'PCC Platinum Elite')?.file?.url + '?width=40';
											} else if (location.level?.toLowerCase() === 'platinum premier') {
												icon = data?.map_icons?.find((icon) => icon.location_type === 'PCC Premiere')?.file?.url + '?width=40';
											}

											const url = showroomUrls.find((showroom) => showroom.showroom_id === location.store_number)?.url || `/where-to-buy/showrooms/${location.page}/`;
											const showroomName = showroomUrls.find((showroom) => showroom.showroom_id === location.store_number)?.page_heading?.replace(/<\/?span[^>]*>/g, '') || location.name;

											return (
												<Marker
													key={index}
													name={location.name}
													ref={(el) => (markerRef.current[index] = el)}
													onClick={() => onMarkerClick(index, markerRef.current[index])}
													position={{
														lat: location.location.coordinates[1],
														lng: location.location.coordinates[0],
													}}
													zIndex={location.type === 'Showroom' ? 2 : selectedPlace !== null && selectedPlace === index ? 3 : 1}
													icon={icon ? { url: icon } : null}
												>
													{selectedPlace === index && (
														<InfoWindow onCloseClick={resetSelectedPlace}>
															<div className='w-64 py-2 pl-2 pr-4 font-sans'>
																{showroomName && <p className='text-base font-semibold leading-tight'>{location.type?.toLowerCase() === 'showroom' ? <Link to={url}>{showroomName}</Link> : showroomName}</p>}

																{location.type?.toLowerCase() === 'showroom' && index === 0 && location.dist?.miles?.value > mapSettings.radius && (
																	<p className='mt-1 text-sm text-gray-60'>
																		<span className='font-semibold'>
																			<Icon
																				name='pin_drop'
																				className={`${styles.locationsClosestIcon} mr-1`}
																			/>{' '}
																			Closest Showroom:
																		</span>{' '}
																		{Math.ceil(location.dist.miles.value)} {Math.ceil(location.dist.miles.value) <= 1 ? 'mile' : 'miles'}
																	</p>
																)}

																<p className={`${styles.locationAddress} mb-2 mt-1`}>
																	<span className='block'>{location.line_1}</span>
																	{location.line_2 ? <span className='block'>{location.line_2}</span> : ''}
																	<span className='block'>
																		{location.city}, {location.state} {location.postal_code}
																	</span>
																</p>

																{location.type?.toLowerCase() === 'showroom' && (
																	<Link
																		className={`${styles.locationLink} text-sm font-semibold`}
																		to={!data.pro_map ? '/where-to-buy/schedule-consultation/' : '/professionals/request-project-quote/'}
																	>
																		{!data.pro_map ? 'Schedule Appointment' : 'Get A Quote'}
																	</Link>
																)}

																{location.type?.toLowerCase() === 'lowes' && lowesLocations.indexOf(location.postal_code) > -1 === true && (
																	<a
																		href={'https://www.pellaatlowes.com/experiencePella/'}
																		target='_blank'
																		rel='noopener noreferrer'
																		className={`${styles.locationLink} text-sm font-semibold`}
																		title='This opens Experience Pella In-store in a new window'
																	>
																		Experience Pella in-store
																	</a>
																)}
															</div>
														</InfoWindow>
													)}
												</Marker>
											);
										})}
								</GoogleMap>
							)}
						</div>

						<div className='px-4 md:px-0'>
							{data?.address && (
								<div className='my-6 xl:mt-0'>
									{data?.subheading?.[0] && <p className='mb-3 bg-gray-10 px-6 py-2 text-base font-bold uppercase md:px-2 md:text-lg'>{data.subheading[0]}</p>}
									<div className='flex flex-row flex-wrap items-start px-6 md:px-0'>
										{data?.file?.url && (
											<Image
												data={{ image: data.file }}
												className='flex-intial'
											/>
										)}
										<div className='ml-4 flex-1'>
											<div dangerouslySetInnerHTML={{ __html: data?.address }} />
											<Button
												color='tertiary'
												onClick={onCorporateViewClick}
												className='mt-2 !w-auto justify-start'
											>
												{data?.view_on_map}
											</Button>
										</div>
									</div>
								</div>
							)}

							<form
								id='js-wtbLocationForm'
								className='mb-4 flex w-full items-end'
								onSubmit={onZipFormSubmit}
							>
								<TextInput
									id='mapZipCode'
									labelText='Zip Code'
									groupClass={`${styles.googleMap_Zip} flex-1`}
									value={postalCode}
									onChange={onPostalCodeChange}
									autoComplete='postal-code'
								/>

								<Button
									type='submit'
									color='primary-alt'
									size='sm'
									className='flex-initial'
								>
									Submit
								</Button>
							</form>

							<Select
								id='mapRadius'
								labelText='Within'
								onChange={onRadiusChange}
								value={mapSettings.radius}
								groupClass={styles.googleMap_Radius}
							>
								<option value={10}>10 miles</option>
								<option value={25}>25 miles</option>
								<option value={50}>50 miles</option>
								<option value={75}>75 miles</option>
								<option value={100}>100 miles</option>
							</Select>

							<Fieldset
								legendText='Filter Locations'
								hideLegend
							>
								<input
									type='checkbox'
									id='showFilters'
									className='hidden'
								/>
								<label
									htmlFor='showFilters'
									className={`${styles.googleMap_FilterHeading} mb-3 inline-flex w-full items-center justify-between md:mb-4`}
								>
									<span className='font-semibold'>Filter Locations</span>
									<Icon
										name='keyboard_arrow_down'
										className='flex-initial md:hidden'
									/>
								</label>

								<div className='hidden md:block'>
									{include.includes('showrooms') && (
										<Checkbox
											id='showrooms'
											group='filter'
											labelText='Pella Showrooms'
											onChange={onCheckboxChange}
											defaultChecked={true}
										/>
									)}

									{include.includes('contractors') && (
										<Checkbox
											id='contractors'
											group='filter'
											labelText='Pella Certified Contractors'
											onChange={onCheckboxChange}
											defaultChecked={true}
										/>
									)}

									{include.includes('lowes') && (
										<Checkbox
											id='lowes'
											group='filter'
											labelText={`Pella at Lowe's`}
											onChange={onCheckboxChange}
											defaultChecked={true}
										/>
									)}

									{include.includes('stores') && (
										<Checkbox
											id='stores'
											group='filter'
											labelText='Pella Lumberyards + Building Suppliers'
											onChange={onCheckboxChange}
											defaultChecked={true}
										/>
									)}
								</div>
							</Fieldset>
						</div>

						<p className={`${styles.locationsHeading} relative inline py-1 pl-4 pr-3 font-semibold uppercase md:mt-8 md:py-2 md:pl-0 md:pr-4`}>
							<span className='relative z-1'>{data?.subheading?.[1] ? data.subheading[1] : 'Locations'}</span>
						</p>

						{isValidArray(directory) && (
							<div className={`${styles.googleMap_Results} ${!loadMore ? 'overflow-auto' : 'overflow-hidden'} relative px-4 md:px-0`}>
								<Row
									className='mt-4'
									noGutters
								>
									{directory.map((location, index) => {
										const url = showroomUrls.find((showroom) => showroom.showroom_id === location.store_number)?.url || `/where-to-buy/showrooms/${location.page}/`;
										const showroomName = showroomUrls.find((showroom) => showroom.showroom_id === location.store_number)?.page_heading?.replace(/<\/?span[^>]*>/g, '') || location.name;

										return (
											<Column
												key={index}
												xs={12}
												md={4}
												xl={3}
											>
												<div className='py-4 md:pr-8'>
													{showroomName && <p className='text-base font-semibold leading-tight'>{location.type?.toLowerCase() === 'showroom' ? <Link to={url}>{showroomName}</Link> : showroomName}</p>}

													{location.type?.toLowerCase() === 'showroom' && index === 0 && location.dist?.miles?.value > mapSettings.radius && (
														<p className='mt-1 text-sm text-gray-60'>
															<span className='font-semibold'>
																<Icon
																	name='pin_drop'
																	className={`${styles.locationsClosestIcon} mr-1`}
																/>{' '}
																Closest Showroom:
															</span>{' '}
															{Math.ceil(location.dist.miles.value)} {Math.ceil(location.dist.miles.value) <= 1 ? 'mile' : 'miles'}
														</p>
													)}

													<p className={`${styles.locationAddress} mb-2 mt-1`}>
														<span className='block'>{location.line_1}</span>
														{location.line_2 ? <span className='block'>{location.line_2}</span> : ''}
														<span className='block'>
															{location.city}, {location.state} {location.postal_code}
														</span>
													</p>

													{location.type?.toLowerCase() === 'showroom' && (
														<Link
															className={`${styles.locationLink} text-sm font-semibold`}
															to={data?.map_type !== 'pro' ? '/where-to-buy/schedule-consultation/' : '/professionals/request-project-quote/'}
														>
															{data?.map_type !== 'pro' ? 'Schedule Appointment' : 'Get A Quote'}
														</Link>
													)}

													{location.type?.toLowerCase() === 'lowes' && lowesLocations.indexOf(location.postal_code) > -1 === true && (
														<a
															href={'https://www.pellaatlowes.com/experiencePella/'}
															target='_blank'
															rel='noopener noreferrer'
															className={`${styles.locationLink} text-sm font-semibold`}
															title='This opens Experience Pella In-store in a new window'
														>
															Experience Pella in-store
														</a>
													)}
												</div>
											</Column>
										);
									})}
								</Row>

								{loadMore && (
									<div className={`${styles.googleMap_LoadMore} absolute w-full px-4 pb-4 pt-20 text-center`}>
										<Button
											color='primary-alt'
											onClick={onLoadMoreClick}
										>
											See More Locations
										</Button>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</Container>
		</div>
	);
};

LocationsMap.propTypes = {
	data: PropTypes.object,
	include: PropTypes.array,
	radius: PropTypes.number,
	className: PropTypes.string,
	containerWidth: PropTypes.number,
};

export default LocationsMap;

export const query = graphql`
	{
		allContentstackBranchShowroom {
			nodes {
				url
				showroom_id
				page_heading
			}
		}
	}
`;
